// A customer order (M7, WP7.2): what was ordered and promised, whether each line is covered
// (from stock, by a batch — linked — or uncovered, with why and a link to the gap on the
// planning board), the batches allocated to it, its statuses and its history with who did
// what. Edit, change a status or cancel it from here; allocate a batch to a line.

import type { OrderDetail } from '@enp/domain/api';
import { canOpenArea } from '@enp/domain/permissions';
import { qty, qtyToString, subQty, sumQty } from '@enp/domain/quantity';
import { addDays } from '@enp/domain/time';
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  notFound,
  useNavigate,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import {
  ArrowLeft,
  Ban,
  CircleCheck,
  ClipboardX,
  ExternalLink,
  History,
  Pencil,
  Plus,
  RefreshCw,
  Undo2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { brand } from '@enp/brand';
import { meQueryOptions } from '../../../../auth/session.js';
import { ApiError } from '../../../../lib/api.js';
import { CancelOrderDialog, StatusDialog } from '../../../../orders/dialogs.js';
import { OrdersBreadcrumb } from '../../../../orders/parts.js';
import {
  changeOrderStatus,
  orderFeasibilityQuery,
  orderQuery,
} from '../../../../orders/queries.js';
import {
  dueText,
  eventText,
  FULFILMENT_STATUS,
  KIND_LABEL,
  money,
  ORDER_STATUS,
  PAYMENT_STATUS,
} from '../../../../orders/words.js';
import { lineNextSteps } from '../../../../planning/board.js';
import { AllocateDialog, formatWhen, type AllocatingLine } from '../../../../planning/dialogs.js';
import { BoardLink, LineCoverage, useNow } from '../../../../planning/parts.js';
import { boardQuery, invalidatePlanning, unallocate } from '../../../../planning/queries.js';
import {
  agoText,
  orderCoverageBadge,
  qtyText,
  shortBy,
  sourcesText,
} from '../../../../planning/words.js';
import { planningProblem } from '../../../../planning/problems.js';
import { PlanBatchDialog, type PlanForLine } from '../../../../production/plan-dialog.js';
import {
  cell,
  Fact,
  Facts,
  numCell,
  Panel,
  PlainTable,
  ProblemNote,
} from '../../../../production/parts.js';
import { KeyHints, usePageKeys } from '../../../../purchasing/parts.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { Notice } from '../../../../stations/page-parts.js';
import { accountToday } from '../../../../stock/labels.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute('/_app/orders/$orderId/')({
  loader: async ({ context, params }) => {
    if (!UUID_RE.test(params.orderId)) throw notFound();
    try {
      await context.queryClient.ensureQueryData(orderQuery(params.orderId));
    } catch (error) {
      if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
        throw notFound();
      }
      throw error;
    }
  },
  component: OrderPage,
  pendingComponent: OrderSkeleton,
  errorComponent: OrderError,
  notFoundComponent: OrderNotFound,
});

function OrderSkeleton() {
  return (
    <div role="status" aria-live="polite" className="flex flex-col gap-4">
      <span className="sr-only">Loading the order…</span>
      <h1 className="sr-only">Loading the order</h1>
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-8 w-72" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-56 w-full" />
    </div>
  );
}

function OrderError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <ErrorPanel
      error={error}
      what="This order"
      onRetry={() => {
        reset();
        void router.invalidate();
      }}
    />
  );
}

function OrderNotFound() {
  useDocumentTitle('Order not found');
  return (
    <>
      <PageHeader eyebrow={<OrdersBreadcrumb page="Not found" />} title="Order not found" />
      <EmptyState
        icon={ClipboardX}
        title="No such order"
        description="It may belong to another branch, or the link is wrong."
        action={
          <Button variant="secondary" asChild>
            <Link to="/orders">
              <ArrowLeft aria-hidden />
              Back to orders
            </Link>
          </Button>
        }
      />
    </>
  );
}

type Line = OrderDetail['lines'][number];

/** How often a new order's page asks whether planning has checked it yet (UI7A-203). */
const CHECK_POLL_MS = 3_000;
/** After this, it says planning hasn't checked it yet, and stops asking. */
const CHECK_WAIT_MS = 60_000;

/** "Checking…" while planning works a new order in (the board's updating style, UI7A-203). */
function CheckingChip({ testId }: { testId?: string }) {
  return (
    <span
      role="status"
      className="inline-flex w-fit items-center gap-1.5 rounded-full border border-border bg-surface-sunken px-2 py-0.5 text-xs font-medium leading-5 text-fg"
      data-testid={testId}
      data-checking="true"
    >
      <RefreshCw aria-hidden className="size-3.5 shrink-0 animate-spin-slow" />
      Checking…
    </span>
  );
}

function OrderPage() {
  const { orderId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const { data: o } = useSuspenseQuery(orderQuery(orderId));
  useDocumentTitle(`${o.number} · ${o.customer_name ?? 'Order'}`);
  const timeZone = me.account.timezone;
  const today = accountToday(me.account);
  const canEdit = me.permissions.includes('orders.edit');
  const canPlan = me.permissions.includes('production.plan');
  const seesPlanning = canOpenArea(me.permissions, 'planning');
  const open = o.order_status === 'draft' || o.order_status === 'confirmed';
  const cancelled = o.order_status === 'cancelled';
  const [acting, setActing] = useState<'status' | 'cancel' | null>(null);
  const [allocating, setAllocating] = useState<AllocatingLine | null>(null);
  const [planning, setPlanning] = useState<PlanForLine | null>(null);
  const run = useQuery(orderFeasibilityQuery(orderId));
  // Confirmed, within the horizon and in a branch, but not in the run yet: planning checks it
  // now (UI7A-203). Say so and ask again every few seconds, rather than "Not checked".
  const beyondHorizon =
    !!o.promised_day &&
    !!run.data?.run &&
    o.promised_day > addDays(run.data.run.business_day, run.data.run.horizon_days - 1);
  const awaitingCheck =
    o.order_status === 'confirmed' &&
    o.coverage === 'not_checked' &&
    o.branch !== null &&
    run.data?.run !== null &&
    !beyondHorizon;
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!awaitingCheck) return;
    const t = setTimeout(() => setGaveUp(true), CHECK_WAIT_MS);
    return () => clearTimeout(t);
  }, [awaitingCheck]);
  const checking = awaitingCheck && !gaveUp;
  useQuery({ ...orderQuery(orderId), refetchInterval: checking ? CHECK_POLL_MS : false });
  useQuery({
    ...orderFeasibilityQuery(orderId),
    enabled: checking,
    refetchInterval: checking ? CHECK_POLL_MS : false,
  });
  const board = useQuery({
    ...boardQuery(o.branch?.id),
    enabled: allocating !== null && o.branch !== null,
  });
  const now = useNow();

  const give = useMutation({
    mutationFn: (a: { batchId: string; id: string }) => unallocate(a.batchId, a.id),
    onSuccess: async () => {
      await invalidatePlanning(queryClient);
      toast.success('Unallocated', { description: 'That output goes to the shelf again.' });
    },
  });
  const giveFailed = give.isError ? planningProblem(give.error, 'It wasn’t unallocated') : null;
  // A draft is confirmed with one press (UI7A-10), not through "Change status…".
  const confirm = useMutation({
    mutationFn: () => changeOrderStatus(orderId, { order_status: 'confirmed' }),
    onSuccess: async (next) => {
      queryClient.setQueryData(orderQuery(orderId).queryKey, next);
      await invalidatePlanning(queryClient);
      toast.success(`${o.number} confirmed`, { description: 'Planning checks it now.' });
    },
  });
  const confirmFailed = confirm.isError
    ? planningProblem(confirm.error, 'The order wasn’t confirmed')
    : null;

  usePageKeys({
    e:
      canEdit && open
        ? () => void navigate({ to: '/orders/$orderId/edit', params: { orderId } })
        : undefined,
    s: canEdit && !cancelled ? () => setActing('status') : undefined,
  });
  const hints: [string, string][] = [
    ...(canEdit && open ? ([['e', 'edit']] as [string, string][]) : []),
    ...(canEdit && !cancelled ? ([['s', 'change status']] as [string, string][]) : []),
  ];

  const due = dueText(o, today);
  const coverage = orderCoverageBadge(o);
  const whyNotChecked =
    o.order_status === 'draft'
      ? 'A draft isn’t a commitment, so planning doesn’t check it. Confirm the order to have it checked.'
      : !open
        ? 'Finished and cancelled orders aren’t checked.'
        : beyondHorizon && run.data?.run
          ? `Promised after the ${run.data.run.horizon_days}-day planning horizon: it’s checked once it’s within it.`
          : checking
            ? 'Planning is checking it now: its coverage shows here in a moment.'
            : o.branch === null
              ? 'It has no branch to hand it over at, so there is no plan to check it against.'
              : 'Planning hasn’t checked it yet. Open planning to have it checked again.';

  return (
    <>
      <PageHeader
        eyebrow={<OrdersBreadcrumb page={o.number} />}
        title={o.customer_name ?? 'Order'}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono">{o.number}</span>
            <span>· {KIND_LABEL[o.kind]}</span>
            <span>
              · Promised {o.promised_at ? formatWhen(o.promised_at, timeZone) : 'with no time'}
            </span>
            {due && (
              <span className={cn(due.late ? 'font-medium text-state-danger' : 'text-muted')}>
                · {due.text}
              </span>
            )}
          </span>
        }
        actions={
          canEdit ? (
            <>
              {o.order_status === 'draft' && (
                <Button
                  icon={<CircleCheck aria-hidden />}
                  loading={confirm.isPending}
                  onClick={() => confirm.mutate()}
                >
                  Confirm order
                </Button>
              )}
              {open && (
                <Button variant="secondary" asChild>
                  <Link to="/orders/$orderId/edit" params={{ orderId }} aria-keyshortcuts="e">
                    <Pencil aria-hidden />
                    Edit
                  </Link>
                </Button>
              )}
              {open && (
                <Button
                  variant="destructive"
                  icon={<Ban aria-hidden />}
                  onClick={() => setActing('cancel')}
                >
                  Cancel order…
                </Button>
              )}
              {!cancelled && (
                <Button
                  variant={o.order_status === 'draft' ? 'secondary' : 'primary'}
                  icon={<RefreshCw aria-hidden />}
                  aria-keyshortcuts="s"
                  onClick={() => setActing('status')}
                >
                  Change status…
                </Button>
              )}
            </>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2" aria-label="Statuses" role="group">
            <StatusBadge status={ORDER_STATUS[o.order_status].status}>
              Order: {ORDER_STATUS[o.order_status].label}
            </StatusBadge>
            {cancelled ? (
              // Nothing is owed once cancelled (UI7A-10): never an amber "Unpaid".
              <StatusBadge status="neutral">
                Payment:{' '}
                {o.payment_status === 'unpaid'
                  ? 'No payment due'
                  : `${PAYMENT_STATUS[o.payment_status].label}: refund outside ${brand.shortName}`}
              </StatusBadge>
            ) : (
              <StatusBadge status={PAYMENT_STATUS[o.payment_status].status}>
                Payment: {PAYMENT_STATUS[o.payment_status].label}
              </StatusBadge>
            )}
            {/* A cancelled order is never handed over (UI7A-111): no handover state to show. */}
            {!cancelled && (
              <StatusBadge status={FULFILMENT_STATUS[o.fulfilment_status].status}>
                Handover: {FULFILMENT_STATUS[o.fulfilment_status].label}
              </StatusBadge>
            )}
          </div>
          <KeyHints keys={hints} />
        </div>

        {cancelled && (
          <Notice tone="danger" title="Cancelled: nothing is owed or made">
            Cancelled {o.cancelled_at && `${formatWhen(o.cancelled_at, timeZone)} `}
            {cancelledBy(o)}
            {o.cancel_reason ? `: “${o.cancel_reason}”` : ''}. Whatever was allocated or held for it
            went back to the shelf.
          </Notice>
        )}

        {confirmFailed && <ProblemNote problem={confirmFailed} focus />}
        <Panel
          id="coverage"
          title="Can it be made in time?"
          description={
            // Only when it was checked (UI7A-10): a draft or a cancelled order never was.
            run.data?.run && o.coverage !== 'not_checked'
              ? `Checked ${agoText(run.data.run.computed_at, now)} against ${run.data.run.branch.name}’s plan and stock.`
              : undefined
          }
          actions={
            seesPlanning && open ? (
              <Button variant="secondary" asChild>
                <Link to="/planning" search={o.branch ? { branch: o.branch.id } : {}}>
                  Open planning
                </Link>
              </Button>
            ) : undefined
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            {checking ? (
              <CheckingChip testId="order-coverage" />
            ) : (
              <StatusBadge status={coverage.status} data-testid="order-coverage">
                {coverage.label}
              </StatusBadge>
            )}
            <span className="text-body text-fg">
              {o.coverage === 'not_checked' ? whyNotChecked : (coverageDetail(o) ?? coverage.hint)}
            </span>
          </div>
        </Panel>

        <Panel id="lines" title="What they ordered">
          <PlainTable
            label="Order lines"
            head={['#', 'Item', 'Amount', 'Price each', 'Line total', 'Coverage']}
            align={['left', 'left', 'right', 'right', 'right', 'left']}
            foot={
              <tr>
                <td className={cell} colSpan={4}>
                  <span className="font-medium text-fg">Total (incl. VAT)</span>
                </td>
                <td className={numCell}>
                  <span
                    className={cn(
                      'font-semibold',
                      cancelled ? 'text-muted line-through' : 'text-fg',
                    )}
                    data-testid="order-total"
                  >
                    {money(o.total_cents)}
                  </span>
                  {cancelled && <span className="block text-xs text-muted">Not owed</span>}
                </td>
                <td className={cell} />
              </tr>
            }
          >
            {o.lines.map((l) => (
              <LineRow
                key={l.id}
                l={l}
                order={o}
                canPlan={canPlan && open}
                seesPlanning={seesPlanning}
                onAllocate={setAllocating}
                onPlan={setPlanning}
                onUnallocate={(batchId, id) => give.mutate({ batchId, id })}
                unallocating={give.isPending ? give.variables?.id : undefined}
                timeZone={timeZone}
                notInRun={checking ? 'checking' : open ? 'not_checked' : 'closed'}
              />
            ))}
          </PlainTable>
          {giveFailed && <ProblemNote problem={giveFailed} focus />}
        </Panel>

        <Panel id="details" title="Details">
          <Facts>
            <Fact label="Kind">{KIND_LABEL[o.kind]}</Fact>
            <Fact label={o.fulfilment_status === 'fulfilled' ? 'Handed over at' : 'Hand over at'}>
              {o.branch?.name ?? '—'}
            </Fact>
            <Fact label="Promised">
              {o.promised_at ? formatWhen(o.promised_at, timeZone) : 'No promised time'}
            </Fact>
            <Fact label="Ordered">{formatWhen(o.ordered_at, timeZone)}</Fact>
            <Fact label="Taken by">{o.created_by?.name ?? 'Someone'}</Fact>
            <Fact label="Note">{o.note ?? <span className="text-muted">None</span>}</Fact>
          </Facts>
        </Panel>

        <Panel id="history" title="History" description="Every change, with who made it.">
          <ol aria-label="History" className="flex flex-col gap-3">
            {[...o.history].reverse().map((e) => (
              <li key={e.id} className="flex items-start gap-3">
                <History aria-hidden className="mt-0.5 size-4 shrink-0 text-muted" />
                <div className="flex min-w-0 flex-col gap-0.5">
                  <p className="text-body text-fg">
                    {eventText(e, (iso) => formatWhen(iso, timeZone))}
                  </p>
                  {e.note && <p className="text-sm text-fg">“{e.note}”</p>}
                  <p className="text-sm text-muted">
                    {e.by?.name ?? 'Someone'} · {formatWhen(e.occurred_at, timeZone)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </Panel>
      </div>

      {acting === 'status' && <StatusDialog order={o} onClose={() => setActing(null)} />}
      {acting === 'cancel' && <CancelOrderDialog order={o} onClose={() => setActing(null)} />}
      {allocating && !board.data && (
        <PlanLoading
          failed={board.isError}
          onRetry={() => void board.refetch()}
          onClose={() => setAllocating(null)}
        />
      )}
      {allocating && board.data && (
        <AllocateDialog
          from="line"
          line={allocating}
          board={board.data}
          timeZone={timeZone}
          onClose={() => setAllocating(null)}
          onPlanForLine={(l) =>
            setPlanning({
              order_line_id: l.order_line_id,
              order_number: l.order_number,
              position: l.position,
              label: l.label,
              product_id: l.product_id,
              open: l.open,
              promised_at: l.promised_at,
            })
          }
        />
      )}
      {planning && (
        <PlanBatchDialog
          day={o.promised_day ?? today}
          today={today}
          timeZone={timeZone}
          branchId={o.branch?.id}
          search={planning.product_id ? planning.label : undefined}
          forLine={planning}
          onClose={() => setPlanning(null)}
          onPlanned={(b) => {
            setPlanning(null);
            void invalidatePlanning(queryClient);
            toast.success(
              `Batch ${b.number} planned for ${planning.order_number} line ${planning.position}`,
              {
                description: `${b.product.name}, ${formatWhen(b.planned_start, timeZone)}. Planning checks it now.`,
              },
            );
          }}
        />
      )}
    </>
  );
}

/** While the plan loads for "Allocate a batch…", or when it didn't. */
function PlanLoading({
  failed,
  onRetry,
  onClose,
}: {
  failed: boolean;
  onRetry: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{failed ? 'The plan didn’t load' : 'Loading the plan…'}</DialogTitle>
          <DialogDescription>
            {failed
              ? 'The batches to choose from come from the planning board. Try again, or close this.'
              : 'Finding the planned batches that could make this line.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
          {failed && (
            <Button type="button" onClick={onRetry}>
              Try again
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "Line 1 is uncovered; line 2 is short by 2; line 3 is at risk: see why below." */
function coverageDetail(o: OrderDetail): string | null {
  const parts = o.lines.flatMap((l) => {
    const c = l.coverage;
    if (!c) return [];
    // Partly covered says how short, never "uncovered" (UI7A-109).
    if (c.status === 'uncovered' && Number(c.covered_qty) > 0) {
      return [`line ${l.position} is short by ${shortBy(c)}`];
    }
    if (c.status === 'uncovered') return [`line ${l.position} is uncovered`];
    if (c.status === 'not_evaluated') return [`line ${l.position} couldn’t be checked`];
    if (c.at_risk) return [`line ${l.position} is at risk`];
    return [];
  });
  if (parts.length === 0) return null;
  const text = parts.join('; ');
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}: see why below.`;
}

function cancelledBy(o: OrderDetail): string {
  const e = [...o.history].reverse().find((x) => x.kind === 'cancelled');
  return e?.by?.name ? `by ${e.by.name}` : '';
}

function LineRow({
  l,
  order,
  canPlan,
  seesPlanning,
  onAllocate,
  onPlan,
  onUnallocate,
  unallocating,
  timeZone,
  notInRun,
}: {
  l: Line;
  order: OrderDetail;
  canPlan: boolean;
  seesPlanning: boolean;
  onAllocate: (line: AllocatingLine) => void;
  onPlan: (line: PlanForLine) => void;
  onUnallocate: (batchId: string, id: string) => void;
  unallocating: string | undefined;
  timeZone: string;
  /** What a line not in the run says (UI7A-203, UI7A-205). */
  notInRun: 'checking' | 'not_checked' | 'closed';
}) {
  const [asking, setAsking] = useState<string | null>(null);
  const unit = l.product?.base_uom ?? 'ea';
  const what = l.product?.name ?? l.custom_description ?? 'Custom item';
  const left = subQty(l.qty, sumQty(l.allocations.map((a) => a.qty)));
  const open = left.isNegative() ? '0' : qtyToString(left);
  const c = l.coverage;
  const covered = c && (c.status === 'covered_by_batch' || c.status === 'covered_by_stock');
  const from = c ? sourcesText(c, unit) : null;
  const branch = order.branch?.id;
  const productOf = (batchId: string) =>
    l.allocations.find((a) => a.batch.id === batchId)?.batch.product.name;
  const next = c ? lineNextSteps(null, c) : { planForLine: false, planMore: null, root: null };
  // A custom item with no batch planned for it takes none (UI7A-111): plan one instead.
  const canAllocate =
    !!c &&
    !covered &&
    c.status !== 'not_evaluated' &&
    c.reason !== 'custom_item_unplanned' &&
    qty(open).greaterThan(0);
  const planFor = (open: string): PlanForLine => ({
    order_line_id: l.id,
    order_number: order.number,
    position: l.position,
    label: what,
    product_id: l.product?.id ?? null,
    open,
    promised_at: order.promised_at,
  });
  return (
    <tr>
      <td className={cn(cell, 'w-10 tabular-nums text-muted')}>{l.position}</td>
      <td className={cn(cell, 'min-w-48')}>
        <span className="flex flex-col gap-0.5 leading-tight">
          <span className="font-medium text-fg">
            {what}
            {!l.product && <span className="font-normal text-muted"> (custom)</span>}
          </span>
          {l.note && <span className="text-sm text-muted">“{l.note}”</span>}
          {l.allocations.map((a) => (
            <span key={a.id} className="flex flex-wrap items-center gap-x-2 text-sm text-fg">
              <span>
                From{' '}
                <Link
                  to="/production/$batchId"
                  params={{ batchId: a.batch.id }}
                  className="font-mono text-primary-text underline underline-offset-2"
                >
                  {a.batch.number}
                </Link>{' '}
                ({a.batch.product.name}, {formatWhen(a.batch.planned_start, timeZone)}):{' '}
                <span className="tabular-nums">{qtyText(a.qty, a.batch.product.base_uom)}</span>
              </span>
              {canPlan &&
                (a.batch.status === 'planned' || a.batch.status === 'started') &&
                (asking === a.id ? (
                  <span className="flex flex-wrap items-center gap-2" role="group">
                    <span className="text-sm">This line loses its cover. Unallocate?</span>
                    {/* Cancel, then the destructive one last (UI8C-10). */}
                    <Button size="sm" variant="secondary" onClick={() => setAsking(null)}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="destructiveSolid"
                      loading={unallocating === a.id}
                      onClick={() => onUnallocate(a.batch.id, a.id)}
                    >
                      Yes, unallocate
                    </Button>
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Undo2 aria-hidden />}
                    onClick={() => setAsking(a.id)}
                  >
                    Unallocate…
                  </Button>
                ))}
            </span>
          ))}
          {l.reservations
            .filter((r) => r.status === 'soft' || r.status === 'claimed')
            .map((r) => (
              <span key={r.id} className="text-sm text-muted">
                Held: {qtyText(r.qty, unit)}
                {r.lot ? ` from lot ${r.lot.lot_code}` : ''}
              </span>
            ))}
        </span>
      </td>
      <td className={numCell}>{qtyText(l.qty, unit)}</td>
      <td className={numCell}>{money(l.unit_price_cents)}</td>
      <td className={numCell}>{money(l.line_total_cents)}</td>
      <td className={cn(cell, 'min-w-60')}>
        {c ? (
          <LineCoverage
            line={c}
            productOfBatch={productOf}
            linkBatch={
              seesPlanning
                ? (batchId, number) => (
                    <BoardLink focus={batchId} branch={branch} className="font-mono">
                      {number}
                    </BoardLink>
                  )
                : undefined
            }
            from={covered ? from : null}
            actions={
              <>
                {seesPlanning && (
                  <BoardLink focus={c.order_line_id} branch={branch} className="text-sm">
                    {covered ? 'See it on the planning board' : 'See the gap on the planning board'}
                  </BoardLink>
                )}
                {/* Never an uncovered line without a way forward (UI7A-105). */}
                {canPlan &&
                  (next.planForLine || next.planMore !== null || next.root || canAllocate) && (
                    <span
                      className="mt-1 flex flex-wrap items-center gap-2"
                      data-testid="line-actions"
                    >
                      {/* Outlined, like every row action (UI8C-13). */}
                      {next.planForLine && (
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={<Plus aria-hidden />}
                          onClick={() => onPlan(planFor(l.qty))}
                        >
                          Plan a batch for this line…
                        </Button>
                      )}
                      {next.planMore !== null && (
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={<Plus aria-hidden />}
                          onClick={() => onPlan(planFor(next.planMore as string))}
                        >
                          Plan {next.planMore} more…
                        </Button>
                      )}
                      {next.root && seesPlanning && (
                        <Button size="sm" variant="secondary" asChild>
                          <Link
                            to="/planning"
                            search={{ focus: next.root.batch_id, ...(branch ? { branch } : {}) }}
                          >
                            <ExternalLink aria-hidden />
                            Open {next.root.number}
                          </Link>
                        </Button>
                      )}
                      {canAllocate && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            onAllocate({
                              order_line_id: l.id,
                              order_id: order.id,
                              order_number: order.number,
                              position: l.position,
                              product_id: l.product?.id ?? null,
                              label: what,
                              qty: l.qty,
                              promised_at: order.promised_at,
                              open,
                            })
                          }
                        >
                          Allocate a batch…
                        </Button>
                      )}
                    </span>
                  )}
              </>
            }
          />
        ) : notInRun === 'checking' ? (
          <CheckingChip />
        ) : (
          // A finished or cancelled order is closed, as its summary says (UI7A-205).
          <StatusBadge status="neutral" data-testid="line-coverage-closed">
            {notInRun === 'closed' ? 'Closed' : 'Not checked'}
          </StatusBadge>
        )}
      </td>
    </tr>
  );
}
